/**
 * Flowchart utility functions for data validation, conversion, and manipulation
 */

/**
 * Validate flowchart data structure
 */
export const validateFlowchartData = (flowchartData) => {
  const errors = []
  
  if (!flowchartData) {
    errors.push('Flowchart data is required')
    return { isValid: false, errors }
  }
  
  if (!flowchartData.jsonData) {
    errors.push('JSON data is required')
  } else {
    const { nodes, edges } = flowchartData.jsonData
    
    if (!Array.isArray(nodes)) {
      errors.push('Nodes must be an array')
    } else if (nodes.length === 0) {
      errors.push('At least one node is required')
    } else {
      // Validate each node
      nodes.forEach((node, index) => {
        if (!node.id) {
          errors.push(`Node ${index} is missing an ID`)
        }
        if (!node.type) {
          errors.push(`Node ${index} is missing a type`)
        }
        if (!node.position || typeof node.position.x !== 'number' || typeof node.position.y !== 'number') {
          errors.push(`Node ${index} has invalid position`)
        }
      })
    }
    
    if (!Array.isArray(edges)) {
      errors.push('Edges must be an array')
    } else {
      // Validate each edge
      edges.forEach((edge, index) => {
        if (!edge.id) {
          errors.push(`Edge ${index} is missing an ID`)
        }
        if (!edge.source || !edge.target) {
          errors.push(`Edge ${index} is missing source or target`)
        }
      })
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Create default flowchart data structure
 */
export const createDefaultFlowchartData = () => {
  return {
    imageData: null,
    jsonData: {
      nodes: [],
      edges: [],
      viewport: { x: 0, y: 0, zoom: 1 }
    },
    lastModified: new Date().toISOString()
  }
}

/**
 * Create a sample flowchart for testing
 */
export const createSampleFlowchartData = () => {
  return {
    imageData: null,
    jsonData: {
      nodes: [
        {
          id: 'start',
          type: 'input',
          position: { x: 100, y: 100 },
          data: { label: 'Start' }
        },
        {
          id: 'process1',
          type: 'default',
          position: { x: 300, y: 100 },
          data: { label: 'Process Step 1' }
        },
        {
          id: 'decision',
          type: 'decision',
          position: { x: 500, y: 100 },
          data: { label: 'Decision Point' }
        },
        {
          id: 'end',
          type: 'output',
          position: { x: 700, y: 200 },
          data: { label: 'End' }
        }
      ],
      edges: [
        {
          id: 'e1',
          source: 'start',
          target: 'process1',
          type: 'default',
          label: ''
        },
        {
          id: 'e2',
          source: 'process1',
          target: 'decision',
          type: 'default',
          label: ''
        },
        {
          id: 'e3',
          source: 'decision',
          target: 'end',
          type: 'default',
          label: 'Yes'
        }
      ],
      viewport: { x: 0, y: 0, zoom: 1 }
    },
    lastModified: new Date().toISOString()
  }
}

/**
 * Merge flowchart data with note data
 */
export const mergeFlowchartWithNote = (noteData, flowchartData) => {
  return {
    ...noteData,
    flowchartData,
    noteType: 'FLOWCHART',
    content: '' // Single-purpose notes have empty content
  }
}

/**
 * Extract flowchart data from note
 */
export const extractFlowchartFromNote = (note) => {
  if (!note || !note.flowchartData) {
    return null
  }
  
  return note.flowchartData
}

/**
 * Check if a note contains flowchart data
 */
export const isFlowchartNote = (note) => {
  return note && note.flowchartData && note.noteType === 'FLOWCHART'
}

/**
 * Get flowchart statistics
 */
export const getFlowchartStats = (flowchartData) => {
  if (!flowchartData || !flowchartData.jsonData) {
    return { nodes: 0, edges: 0, lastModified: null }
  }
  
  const { nodes, edges } = flowchartData.jsonData
  
  return {
    nodes: nodes ? nodes.length : 0,
    edges: edges ? edges.length : 0,
    lastModified: flowchartData.lastModified
  }
}

/**
 * Clean up flowchart data (remove unused fields, validate structure)
 */
export const cleanupFlowchartData = (flowchartData) => {
  if (!flowchartData) {
    return createDefaultFlowchartData()
  }
  
  const cleaned = {
    imageData: flowchartData.imageData || null,
    jsonData: {
      nodes: flowchartData.jsonData?.nodes || [],
      edges: flowchartData.jsonData?.edges || [],
      viewport: flowchartData.jsonData?.viewport || { x: 0, y: 0, zoom: 1 }
    },
    lastModified: flowchartData.lastModified || new Date().toISOString()
  }
  
  // Remove any nodes or edges with invalid IDs
  const validNodeIds = new Set(cleaned.jsonData.nodes.map(node => node.id).filter(Boolean))
  
  cleaned.jsonData.edges = cleaned.jsonData.edges.filter(edge => 
    validNodeIds.has(edge.source) && validNodeIds.has(edge.target)
  )
  
  return cleaned
}
